import React, { useRef, useState } from "react";
import { Card } from "react-bootstrap";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import { organizationApi } from "../../../../../../../../services/Api/Organization/organizationApi";
import {
	SingleApplicationAccess,
	SupportAccessFormProps,
} from "../../../../../../../../services/Api/Organization/organizationTypes";
import { selectOrganization } from "../../../../../../../../services/organizations/selectors";
import { defaultSupportAccessPermissions, isSupportAccessActive, mapSupportAccessPermissions } from "../../utils";
import { OrganizationApplicationAccesses } from "../SupportAccessPreview";
import SupportAccessFormActions from "./SupportAccessFormActions";
import SupportAccessFormInputs from "./SupportAccessFormInputs";

interface Props {
	appsAccesses: OrganizationApplicationAccesses[];
	handleRefreshHistoryTable: () => void;
}

const SupportAccessForm = ({ appsAccesses, handleRefreshHistoryTable }: Props) => {
	const organization = useSelector(selectOrganization);
	const defaultFormValues = {
		applications: appsAccesses.map((app) => {
			return {
				...app,
				permissions: mapSupportAccessPermissions(appsAccesses, app.type, app.permissions),
				enable_access: isSupportAccessActive(app.support_access_to, organization.timezone),
			};
		}),
	};
	const form = useForm<SupportAccessFormProps>({
		criteriaMode: "all",
		defaultValues: defaultFormValues,
	});
	const defaultValues = useRef(defaultFormValues);
	const { addFlash, addSuccessFlash } = useFlash();
	const [loading, setLoading] = useState(false);
	const [removeLoading, setRemoveLoading] = useState(false);
	const { t } = useTranslation();
	const { formState, control, handleSubmit, setError, reset, watch } = form;
	const { fields } = useFieldArray({
		control,
		name: "applications",
		keyName: "key",
	});

	const getParsedFormData = (data: SupportAccessFormProps, idMap: Map<number, number>) => {
		const newData = {
			applications: data.applications?.map((app) => {
				return {
					...app,
					permissions: app.permissions?.filter((f) => f),
				};
			}),
		};
		const formatted = JSON.parse(JSON.stringify(newData));
		const applicationsWithAccess: Record<string, any>[] = [];
		for (let i = 0; i < formatted.applications.length; i++) {
			const app: Record<string, any> = formatted.applications[i];
			idMap.set(applicationsWithAccess.length, i);
			if (app.enable_access) {
				applicationsWithAccess.push(app);
			} else {
				app.support_access_to = undefined;
				applicationsWithAccess.push(app);
			}
		}
		const processedData: SupportAccessFormProps = {
			...formatted,
			applications: applicationsWithAccess,
		};

		return {
			...processedData,
			applications: processedData.applications
				.map((access: SingleApplicationAccess) => {
					if (!access.support_access_to) {
						return {
							...access,
							support_access_to: "",
						};
					}
					return {
						...access,
						support_access_to: access.support_access_to ?? "",
					};
				})
				.filter((app) => app.user_permissions.includes("MANAGE")),
		};
	};

	const save = async (data: SupportAccessFormProps) => {
		const idMap: Map<number, number> = new Map();
		const newData = getParsedFormData(data, idMap);

		try {
			const resp = await organizationApi.updateSupportAccess(newData);
			const resettedData = {
				applications: resp.data.data.applications.map((app: OrganizationApplicationAccesses) => {
					return {
						...app,
						permissions: mapSupportAccessPermissions(appsAccesses, app.type, app.permissions),
						enable_access: isSupportAccessActive(app.support_access_to),
					};
				}),
			};
			reset(resettedData);
			defaultValues.current = resettedData;
			handleRefreshHistoryTable();
			addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
		} catch (e) {
			if (e.errors) {
				e.errors.forEach((error: any) => {
					if (error.field && typeof error.field === "string" && error.field.startsWith("applications[")) {
						const bracketStartIndex = error.field.indexOf("[");
						const bracketEndIndex = error.field.indexOf("]");
						if (bracketStartIndex && bracketEndIndex) {
							const errorIndex = error.field.substring(bracketStartIndex + 1, bracketEndIndex);
							const properIndex = idMap.get(parseInt(errorIndex));
							if (properIndex) {
								error.field = error.field.replace(errorIndex, properIndex.toString());
							}
						}
					}
				});
			}
			handleError.form(e, setError, addFlash);
		}
		setLoading(false);
		setRemoveLoading(false);
	};

	const onSubmit = handleSubmit(async (data) => {
		setLoading(true);
		await save(data);
	});

	return (
		<FormDirty formState={formState} backPath={"RELOAD"} onSubmit={onSubmit} loading={loading}>
			{fields.map((field, index) => {
				const enableAccess = watch(`applications.${index}.enable_access`);
				let permission = appsAccesses.find((f) => f.type === appsAccesses.find((f) => f.id === field.id)?.type);
				if (permission) {
					permission = {
						...permission,
						permissions: Array.from(
							new Set([...defaultSupportAccessPermissions, ...permission?.permissions])
						),
					};
				}

				return (
					<Card className="mb-3" key={fields[index].key}>
						<Card.Body>
							<h5>{appsAccesses.find((f) => f.id === field.id)?.type}</h5>
							{enableAccess && (
								<SupportAccessFormInputs
									index={index}
									field={field}
									permission={permission}
									form={form}
								/>
							)}
							<Controller
								name={`applications.${index}.enable_access`}
								control={control}
								defaultValue={false}
								render={() => (
									<SupportAccessFormActions
										form={form}
										index={index}
										enableAccess={enableAccess}
										field={field}
										removeLoading={removeLoading}
									/>
								)}
							/>
						</Card.Body>
					</Card>
				);
			})}
		</FormDirty>
	);
};

export default SupportAccessForm;

// t(`enums.organization_access.SALE.help_text`);
// t(`enums.organization_access.SALE.label`);
// t(`enums.organization_access.CONFIGURATION.help_text`);
// t(`enums.organization_access.CONFIGURATION.label`);
